import { TGlobalStores } from '.';
import { EShortcutScope } from '@/editor/controller/keyboardController/config';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { difference } from 'lodash-es';
import { isApplePlatform } from '@/utils/browser/platform';

export enum EModifierKey {
  Ctrl = 'ctrl',
  Alt = 'alt',
  Shift = 'shift',
  Meta = 'meta'
}

export enum ESepcialKey {
  Escape = 'escape',
  Blank = ' ',
  Tab = 'tab'
}

export class KeyboardStore {
  private _activeKeys: Set<string | EModifierKey> = new Set();
  private _shortcutScope: EShortcutScope = EShortcutScope.GLOBAL;

  constructor(_gs: TGlobalStores) {}

  public getPressedKeys(): Set<string | EModifierKey> {
    return this._activeKeys;
  }

  public setShortcutScope(scope: EShortcutScope) {
    this._shortcutScope = scope;
  }

  get isInEditorScope() {
    return this._shortcutScope === EShortcutScope.EDITOR;
  }

  public isScopeAllowMoveByKeyboard(): boolean {
    return this._shortcutScope === EShortcutScope.GLOBAL || this._shortcutScope === EShortcutScope.EDITOR;
  }

  get shortcutScope() {
    return this._shortcutScope;
  }

  public isShiftPressed(only: boolean = true): boolean {
    return this.isKeyPressed(EModifierKey.Shift, only);
  }

  /** 多选修饰键：Shift 或平台主修饰键（macOS 为 ⌘，其余为 Ctrl）被单独按住 */
  public isMultiSelectModifierPressed(): boolean {
    const platformModifier = isApplePlatform() ? EModifierKey.Meta : EModifierKey.Ctrl;
    return this.isKeyPressed(EModifierKey.Shift, true) || this.isKeyPressed(platformModifier, true);
  }

  /**
   * OrbitControls 把平移/旋转对调的修饰键
   *
   * three.js 在 `event.ctrlKey || event.metaKey || event.shiftKey` 时就会把 MOUSE.ROTATE 与
   * MOUSE.PAN 互换（OrbitControls.js 的 onMouseDown），不要求单独按住，
   * 所以这里三个键都要看，且不能用 only 模式，否则判断会和它对不上
   */
  public isOrbitSwapModifierPressed(): boolean {
    return (
      this.isKeyPressed(EModifierKey.Shift, false) ||
      this.isKeyPressed(EModifierKey.Ctrl, false) ||
      this.isKeyPressed(EModifierKey.Meta, false)
    );
  }

  /**
   * 检查特定键是否被按住
   * @param only - 是否仅检查该键是否被按住（即没有其他键被按住） 默认值为 `true`
   */
  public isKeyPressed(key: string, only: boolean = true): boolean {
    return this._activeKeys.has(this._normalizeKey(key)) && (!only || this._activeKeys.size === 1);
  }

  public pressKey(key: string) {
    const normalizedKey = this._normalizeKey(key);
    // 长按时浏览器会持续重复派发 keydown，这里只在首次按下时广播，
    // 否则监听方（如按 Tab 切换框选模式）会被重复触发。需要持续响应的场景请轮询 isKeyPressed
    if (this._activeKeys.has(normalizedKey)) return;

    this._activeKeys.add(normalizedKey);
    editorEvent.emit(EditorEventName.KEYBOARD_PRESS_UPDATE); // 触发键盘事件
    editorEvent.emit(EditorEventName.KEYBOARD_KEY_PRESS, normalizedKey);
  }

  public clearKey(key: string) {
    const normalizedKey = this._normalizeKey(key);
    if (this._activeKeys.has(normalizedKey)) {
      this._activeKeys.delete(normalizedKey);
      editorEvent.emit(EditorEventName.KEYBOARD_PRESS_UPDATE); // 触发键盘事件
    }
  }

  /** 检查是否只有指定的按键有被按下（即没有其他键被按下，但这些键里面的某些键可能被按下） */
  public checkKeysPressed(keys: string[]): boolean {
    const checkSet = new Set(keys.map((k) => this._normalizeKey(k)));
    return difference([...this._activeKeys], [...checkSet]).length === 0;
  }

  private _normalizeKey(key: string): string {
    const lowerKey = key.toLowerCase();
    switch (lowerKey) {
      case 'control':
      case 'ctrl':
        return EModifierKey.Ctrl;
      case 'alt':
        return EModifierKey.Alt;
      case 'shift':
        return EModifierKey.Shift;
      case 'meta':
        return EModifierKey.Meta;
      default:
        return lowerKey;
    }
  }

  public clearAllKeys() {
    if (this._activeKeys.size > 0) {
      this._activeKeys.clear();
      editorEvent.emit(EditorEventName.KEYBOARD_PRESS_UPDATE); // 触发键盘事件
    }
  }

  public updateModifierKeys = (event: KeyboardEvent | MouseEvent) => {
    let hasChange = false;

    if (event.shiftKey && !this._activeKeys.has(EModifierKey.Shift)) {
      this._activeKeys.add(EModifierKey.Shift);
      hasChange = true;
    } else if (!event.shiftKey && this._activeKeys.has(EModifierKey.Shift)) {
      this._activeKeys.delete(EModifierKey.Shift);
      hasChange = true;
    }

    if (event.ctrlKey && !this._activeKeys.has(EModifierKey.Ctrl)) {
      this._activeKeys.add(EModifierKey.Ctrl);
      hasChange = true;
    } else if (!event.ctrlKey && this._activeKeys.has(EModifierKey.Ctrl)) {
      this._activeKeys.delete(EModifierKey.Ctrl);
      hasChange = true;
    }

    if (event.altKey && !this._activeKeys.has(EModifierKey.Alt)) {
      this._activeKeys.add(EModifierKey.Alt);
      hasChange = true;
    } else if (!event.altKey && this._activeKeys.has(EModifierKey.Alt)) {
      this._activeKeys.delete(EModifierKey.Alt);
      hasChange = true;
    }

    if (event.metaKey && !this._activeKeys.has(EModifierKey.Meta)) {
      this._activeKeys.add(EModifierKey.Meta);
      hasChange = true;
    } else if (!event.metaKey && this._activeKeys.has(EModifierKey.Meta)) {
      this._activeKeys.delete(EModifierKey.Meta);
      hasChange = true;
    }

    if (hasChange) {
      editorEvent.emit(EditorEventName.KEYBOARD_PRESS_UPDATE); // 触发键盘事件
    }
  };
}
